import React, { useState, useEffect } from 'react';
import { monthlyRewardApi, Top10Winner } from '../services/api';
import { referralApi } from '../services/referralApi';
import { Top10Referrer } from '../types';
import './Top10Popup.css';

interface Top10PopupProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'kiloba' | 'referral';

const Top10Popup: React.FC<Top10PopupProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabType>('kiloba');

  // Kiloba Collection state
  const [kilobaWinners, setKilobaWinners] = useState<Top10Winner[]>([]);
  const [kilobaLoading, setKilobaLoading] = useState(false);
  const [kilobaCompletedMonthStartDate, setKilobaCompletedMonthStartDate] = useState('');
  const [kilobaCompletedMonthEndDate, setKilobaCompletedMonthEndDate] = useState('');
  const [kilobaCurrentMonthEndDate, setKilobaCurrentMonthEndDate] = useState('');
  const [kilobaHasWinners, setKilobaHasWinners] = useState(false);

  // Referral state
  const [referralWinners, setReferralWinners] = useState<Top10Referrer[]>([]);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralCurrentMonthEndDate, setReferralCurrentMonthEndDate] = useState('');
  const [referralHasWinners, setReferralHasWinners] = useState(false);
  const [referralCompletedMonth, setReferralCompletedMonth] = useState('');
  const [referralCompletedYear, setReferralCompletedYear] = useState('');

  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    if (isOpen) {
      loadKilobaTop10();
      loadReferralTop10();
    }
  }, [isOpen]);

  // Countdown timer - uses kiloba end date for both tabs (same timing)
  useEffect(() => {
    const endDate = kilobaCurrentMonthEndDate;
    if (!endDate) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const endTime = new Date(endDate).getTime();
      const distance = endTime - now;

      if (distance < 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setCountdown({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [kilobaCurrentMonthEndDate]);

  const loadKilobaTop10 = async () => {
    setKilobaLoading(true);
    try {
      const response = await monthlyRewardApi.getCurrentMonthTop10();
      if (response.success && response.data) {
        setKilobaWinners(response.data.winners);
        setKilobaCompletedMonthStartDate(response.data.completedMonthStartDate);
        setKilobaCompletedMonthEndDate(response.data.completedMonthEndDate);
        setKilobaCurrentMonthEndDate(response.data.currentMonthEndDate || '');
        setKilobaHasWinners(response.data.hasWinners);
      }
    } catch (error) {
      console.error('Error loading kiloba top 10:', error);
    } finally {
      setKilobaLoading(false);
    }
  };

  const loadReferralTop10 = async () => {
    setReferralLoading(true);
    try {
      const response = await referralApi.getCurrentTop10();
      if (response.success && response.data) {
        setReferralWinners(response.data.winners);
        setReferralHasWinners(response.data.hasWinners);
        setReferralCompletedMonth(String(response.data.completedMonth));
        setReferralCompletedYear(String(response.data.completedYear));
        // Use same end date as kiloba (they're synchronized)
        setReferralCurrentMonthEndDate(kilobaCurrentMonthEndDate);
      }
    } catch (error) {
      console.error('Error loading referral top 10:', error);
    } finally {
      setReferralLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const getReferralVoucherLabel = (rank: number) => {
    if (rank === 1) return 'VOUCHER 500.000đ';
    if (rank === 2) return 'VOUCHER 200.000đ';
    if (rank === 3) return 'VOUCHER 100.000đ';
    return 'VOUCHER 50.000đ';
  };

  if (!isOpen) return null;

  return (
    <div className="top10-popup-overlay" onClick={onClose}>
      <div className="top10-popup-container" onClick={(e) => e.stopPropagation()}>
        <button className="top10-popup-close" onClick={onClose}>
          ✕
        </button>

        {/* Tab Navigation */}
        <div className="top10-tabs">
          <button
            className={`tab-button ${activeTab === 'kiloba' ? 'active' : ''}`}
            onClick={() => setActiveTab('kiloba')}
          >
            🏆 THU THẬP KILOBA
          </button>
          <button
            className={`tab-button ${activeTab === 'referral' ? 'active' : ''}`}
            onClick={() => setActiveTab('referral')}
          >
            👥 GIỚI THIỆU BẠN BÈ
          </button>
        </div>

        {/* Kiloba Collection Tab */}
        {activeTab === 'kiloba' && (
          <>
            <div className="top10-popup-header">
              <h2>🏆 TOP 10 THU THẬP KILOBA</h2>

              {/* Countdown to current month end */}
              {kilobaCurrentMonthEndDate && (
                <div className="top10-countdown">
                  <p className="countdown-label">Kết quả tháng mới sau:</p>
                  <div className="countdown-timer">
                    <div className="countdown-unit">
                      <span className="countdown-value">{countdown.days}</span>
                      <span className="countdown-text">ngày</span>
                    </div>
                    <div className="countdown-unit">
                      <span className="countdown-value">{countdown.hours}</span>
                      <span className="countdown-text">giờ</span>
                    </div>
                    <div className="countdown-unit">
                      <span className="countdown-value">{countdown.minutes}</span>
                      <span className="countdown-text">phút</span>
                    </div>
                    <div className="countdown-unit">
                      <span className="countdown-value">{countdown.seconds}</span>
                      <span className="countdown-text">giây</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="top10-popup-content">
              {kilobaLoading ? (
                <div className="top10-loading">
                  <div className="loading-spinner" />
                  <p>Đang tải...</p>
                </div>
              ) : !kilobaHasWinners ? (
                <div className="top10-empty">
                  <p>Chưa có người chơi nào hoàn thành bộ sưu tập trong kỳ này</p>
                  <p className="empty-hint">Hãy nhanh tay thu thập đủ 5 loại KILOBA để lọt TOP 10 tháng này!</p>
                </div>
              ) : (
                <div className="top10-list">
                  {kilobaWinners.map((winner) => (
                    <div key={winner.userId} className={`top10-item rank-${winner.rank}`}>
                      <div className="top10-rank">
                        <span className="rank-icon">{getRankIcon(winner.rank)}</span>
                      </div>
                      <div className="top10-main">
                        <div className="top10-user">
                          <div className="top10-name">{winner.name}</div>
                        </div>
                        <div className="top10-time">
                          {formatDate(winner.completionTime)}
                        </div>
                        <div className="top10-reward">
                          <span className="reward-icon-top10">🎁</span>
                          <span className="reward-amount-top10">
                            {winner.voucherType === '2m' ? 'VOUCHER GOTIT 2.000.000đ' : 'VOUCHER GOTIT 1.000.000đ'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="top10-popup-footer">
              <p>Thu thập đủ 5 loại KILOBA sớm nhất để nhận voucher lên đến 2.000.000đ!</p>
            </div>
          </>
        )}

        {/* Referral Tab */}
        {activeTab === 'referral' && (
          <>
            <div className="top10-popup-header">
              <h2>👥 TOP 10 GIỚI THIỆU BẠN BÈ</h2>

              {/* Countdown to current month end */}
              {kilobaCurrentMonthEndDate && (
                <div className="top10-countdown">
                  <p className="countdown-label">Kết quả tháng mới sau:</p>
                  <div className="countdown-timer">
                    <div className="countdown-unit">
                      <span className="countdown-value">{countdown.days}</span>
                      <span className="countdown-text">ngày</span>
                    </div>
                    <div className="countdown-unit">
                      <span className="countdown-value">{countdown.hours}</span>
                      <span className="countdown-text">giờ</span>
                    </div>
                    <div className="countdown-unit">
                      <span className="countdown-value">{countdown.minutes}</span>
                      <span className="countdown-text">phút</span>
                    </div>
                    <div className="countdown-unit">
                      <span className="countdown-value">{countdown.seconds}</span>
                      <span className="countdown-text">giây</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="top10-popup-content">
              {referralLoading ? (
                <div className="top10-loading">
                  <div className="loading-spinner" />
                  <p>Đang tải...</p>
                </div>
              ) : !referralHasWinners ? (
                <div className="top10-empty">
                  <p>Chưa có kết quả giới thiệu trong kỳ này</p>
                  <p className="empty-hint">Giới thiệu bạn bè để nhận voucher lên đến 500.000đ!</p>
                </div>
              ) : (
                <div className="top10-list">
                  {referralWinners.map((winner) => (
                    <div key={winner.userId} className={`top10-item rank-${winner.rank}`}>
                      <div className="top10-rank">
                        <span className="rank-icon">{getRankIcon(winner.rank)}</span>
                      </div>
                      <div className="top10-main">
                        <div className="top10-user">
                          <div className="top10-name">{winner.username}</div>
                        </div>
                        <div className="referral-count">
                          <span className="count-icon">👥</span>
                          <span className="count-value">{winner.referralCount} người</span>
                        </div>
                        <div className="top10-reward">
                          <span className="reward-icon-top10">🎁</span>
                          <span className="reward-amount-top10">
                            {getReferralVoucherLabel(winner.rank)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="top10-popup-footer">
              <p>Giới thiệu nhiều bạn bè nhất để nhận voucher lên đến 500.000đ!</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Top10Popup;
